/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  createCustomerAddress,
  getAttributesForm,
  updateCustomerAddress,
  getCountries,
} from '@/account/api';
import {
  defaultShippingCheckBox,
  defaultBillingCheckBox,
} from '@/account/configs/addressField.config';
import {
  AttributesFormModel,
  Country,
  CustomerAddressesModel,
} from '@/account/data/models';
import { getFieldsConfig } from '@/account/lib/getFieldsConfig';
import { getFormValues } from '@/account/lib/getFormValues';
import prepareNormalizedData from '@/account/lib/prepareNormalizedData';
import { serializeAddressForm } from '@/account/lib/serializeAddressForm';
import transformSessionData from '@/account/lib/transformSessionData';
import { useAddressFormProps } from '@/account/types';
import { VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

export const findNonEmptyObject = (
  obj1: Record<string, unknown> | undefined,
  obj2: CustomerAddressesModel | undefined
): CustomerAddressesModel | Record<string, unknown> => {
  if (obj1 && Object.keys(obj1).length > 0) {
    return obj1;
  }

  if (obj2 && Object.keys(obj2).length > 0) {
    return obj2;
  }

  return {};
};

export const useAddressForm = ({
  showFormLoader,
  showSaveCheckBox,
  saveCheckBoxValue,
  addressFormId,
  billingCheckBoxValue,
  shippingCheckBoxValue,
  showShippingCheckBox,
  showBillingCheckBox,
  inputsDefaultValueSet,
  onCloseBtnClick,
  onSuccess,
  onError,
  formName,
}: useAddressFormProps) => {
  const [inLineAlert, setInLineAlert] = useState<{
    text: string;
    type: 'success' | 'warning' | 'error';
    icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  }>({
    text: '',
    type: 'success',
  });
  const [submitLoading, setSubmitLoading] = useState(showFormLoader ?? false);
  const [addressId, setAddressId] = useState<string>(addressFormId || '');
  const [attributesFormList, setAttributesFormList] = useState<
    AttributesFormModel[]
  >([]);
  const [countriesData, setCountriesData] = useState<{
    availableCountries: Country[];
    countriesWithRequiredRegion: string[];
    optionalZipCountries: string[];
  } | null>(null);
  const [inputChange, setInputChange] = useState(() => {
    const savedData = sessionStorage.getItem(`${formName}_addressData`);
    return savedData
      ? { countryCode: JSON.parse(savedData)?.data?.countryCode }
      : inputsDefaultValueSet;
  });
  const [disableField, setDisableField] = useState(false);
  const [saveCheckBoxAddress, setSaveCheckBoxAddress] = useState<boolean>(
    () => {
      const sessionAddressForm = sessionStorage.getItem(
        `${formName}_addressData`
      );

      const sessionAddressFormData = sessionAddressForm
        ? JSON.parse(sessionAddressForm)?.data?.['saveAddressBook']
        : (saveCheckBoxValue as boolean);

      return sessionAddressFormData;
    }
  );

  const handleSaveCheckBoxAddress = useCallback((event: Event) => {
    setSaveCheckBoxAddress((event.target as HTMLInputElement).checked);
  }, []);

  useEffect(() => {
    if (typeof showFormLoader === 'undefined') return;
    setSubmitLoading(showFormLoader);
  }, [showFormLoader]);

  useEffect(() => {
    getAttributesForm(
      addressId ? 'customer_address_edit' : 'customer_register_address'
    ).then((attributeFormResponse) => {
      setAttributesFormList(attributeFormResponse);
    });
  }, [addressId]);

  useEffect(() => {
    setDisableField(true);
    getCountries().then(
      ({
        availableCountries,
        countriesWithRequiredRegion,
        optionalZipCountries,
      }) => {
        setCountriesData({
          availableCountries,
          countriesWithRequiredRegion,
          optionalZipCountries,
        });
        setDisableField(false);
      }
    );
  }, []);

  const countryOptions = useMemo(() => {
    return countriesData?.availableCountries || [];
  }, [countriesData]);

  const isRequiredRegion = useMemo(() => {
    if (!inputChange?.countryCode || !countriesData) return false;
    return countriesData.countriesWithRequiredRegion.includes(
      inputChange.countryCode as string
    );
  }, [inputChange?.countryCode, countriesData]);

  const isRequiredPostCode = useMemo(() => {
    if (!inputChange?.countryCode || !countriesData) return true;
    return !countriesData.optionalZipCountries.includes(
      inputChange.countryCode as string
    );
  }, [inputChange?.countryCode, countriesData]);

  const regionOptions = useMemo(() => {
    if (!inputChange?.countryCode || !countriesData) return [];

    const country = countriesData.availableCountries.find(
      (c) => c.value === inputChange.countryCode
    );

    return (
      country?.availableRegions?.map((region) => ({
        text: region.name,
        value: `${region.code},${region.id}`,
      })) || []
    );
  }, [inputChange?.countryCode, countriesData]);

  const handleOnCloseForm = useCallback(() => {
    setInLineAlert({
      text: '',
      type: 'success',
    });
    onCloseBtnClick?.();
  }, [onCloseBtnClick]);

  const handleUpdateAddress = useCallback(
    async (event: Event, valid: boolean): Promise<void | null | undefined> => {
      if (!valid) return null;

      setSubmitLoading(true);

      const formValues = getFormValues(event.target as HTMLFormElement);
      const serializeValues = serializeAddressForm(formValues);

      await updateCustomerAddress(serializeValues as any)
        .then(() => {
          onSuccess?.();
          onCloseBtnClick?.();
          (event?.target as HTMLFormElement)?.reset();
        })
        .catch((error) => {
          setInLineAlert((prev) => ({
            ...prev,
            text: error.message,
            type: 'error',
          }));
          onError?.(error);
        })
        .finally(() => {
          setAddressId('');
          setSubmitLoading(false);
        });
    },
    [onCloseBtnClick, onError, onSuccess]
  );

  const handleCreateAddress = useCallback(
    async (event: Event, valid: boolean): Promise<void | null | undefined> => {
      if (!valid) return;

      setSubmitLoading(true);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { saveAddressBook, ...formValues } = getFormValues(
        event.target as HTMLFormElement
      );

      const serializeValues = serializeAddressForm(formValues);

      await createCustomerAddress(serializeValues as any)
        .then(() => {
          onSuccess?.();
          onCloseBtnClick?.();
          (event?.target as HTMLFormElement)?.reset();
        })
        .catch((error) => {
          setInLineAlert((prev) => ({
            ...prev,
            text: error.message,
            type: 'error',
          }));
          onError?.(error);
        })
        .finally(() => {
          setAddressId('');
          setSubmitLoading(false);
        });
    },
    [onCloseBtnClick, onError, onSuccess]
  );

  const normalizeFieldsConfig = useMemo(() => {
    if (!attributesFormList.length) return [];

    const shippingCheckBox = {
      ...defaultShippingCheckBox,
      defaultValue: shippingCheckBoxValue ?? false,
      isHidden:
        showSaveCheckBox && !saveCheckBoxAddress ? true : !showShippingCheckBox,
    };

    const billingCheckBox = {
      ...defaultBillingCheckBox,
      defaultValue: billingCheckBoxValue ?? false,
      isHidden:
        showSaveCheckBox && !saveCheckBoxAddress ? true : !showBillingCheckBox,
    };

    const updatedAttributesFormList = [
      ...attributesFormList,
      shippingCheckBox,
      billingCheckBox,
    ];

    const sessionAddressForm = sessionStorage.getItem(
      `${formName}_addressData`
    );

    const sessionAddressFormData = sessionAddressForm
      ? transformSessionData(
          JSON.parse(sessionAddressForm).data as CustomerAddressesModel,
          regionOptions
        )
      : {};

    const fields = getFieldsConfig(
      findNonEmptyObject(sessionAddressFormData, inputsDefaultValueSet),
      updatedAttributesFormList
    );

    return prepareNormalizedData({
      fields,
      addressId,
      countryOptions,
      disableField,
      regionOptions,
      isRequiredRegion,
      isRequiredPostCode,
    });
  }, [
    attributesFormList,
    shippingCheckBoxValue,
    showSaveCheckBox,
    saveCheckBoxAddress,
    showShippingCheckBox,
    billingCheckBoxValue,
    showBillingCheckBox,
    formName,
    regionOptions,
    inputsDefaultValueSet,
    addressId,
    countryOptions,
    disableField,
    isRequiredRegion,
    isRequiredPostCode,
  ]);

  const handleInputChange = useCallback(
    (values: Record<string, string | number | boolean>) => {
      setInputChange((prev) => ({ ...prev, ...values }));
    },
    []
  );

  return {
    regionOptions,
    saveCheckBoxAddress,
    inLineAlert,
    addressId,
    submitLoading,
    normalizeFieldsConfig,
    handleSaveCheckBoxAddress,
    handleUpdateAddress,
    handleCreateAddress,
    handleOnCloseForm,
    handleInputChange,
  };
};
